import {defineMessages} from 'react-intl';

import findKey from 'lodash/findKey';
import flow from 'lodash/fp/flow';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import map from 'lodash/map';
import uniq from 'lodash/uniq';
import values from 'lodash/values';
import moment from 'moment';

import {
  AIRLINE_ID, // DEPRECATED (HZFE_139) - remove this import when feature toggle is removed
  AIRLINE_IDS,
  AIRPORT_ID,
  FLIGHT_NUMBER_LIKE, // DEPRECATED (HZFE_139) - remove this import when feature toggle is removed
  FLIGHT_NUMBERS,
  RAILWAY,
  TRAIN_NUMBER,
} from '@components/Person/PersonOptionsSelection/form-constants';
import {FORM_FIELDS} from '@components/Person/PersonOptionsSelection/MapPeopleFilter/PersonLocationTypesSelector/constants';

import {FEATURES} from '@constants/features';
import {
  VIEW_ACCESS_POINTS,
  VIEW_CRISIS_SIGNAL_LOCATIONS,
  VIEW_EXPATRIATIONS,
  VIEW_PERSON_TYPES,
  VIEW_PERSONS,
  VIEW_PERSONS_GEOCODED_ADDRESS,
  VIEW_PERSONS_MOBILE_LOCATION,
  VIEW_SAFETY_CHECKIN_SIGNAL_LOCATIONS,
  VIEW_SITES,
  VIEW_TRIPS,
} from '@constants/permissions';
import {PERSON_EXPORT_DATA_FIELD_NAMES, PERSON_EXPORT_DATA_OPTIONS} from '@constants/persons';

import localization from '@services/localization';

import {featureToggle} from '@utils/featureToggle/featureToggle';
import horizonJSON from '@utils/json';
import {horizonLocalStorage} from '@utils/local-storage';

import parsePagedResults from '../../utils/parsePagedResults';

const addressAccuracyMessages = defineMessages({
  unknown: {
    id: 'redux.person.utils.addressAccuracy.unknown',
    defaultMessage: 'Unknown',
  },
  notGeocoded: {
    id: 'redux.person.utils.addressAccuracy.notGeocoded',
    defaultMessage: 'Not Geocoded',
  },
  userProvided: {
    id: 'redux.person.utils.addressAccuracy.userProvided',
    defaultMessage: 'User Provided',
  },
  street: {
    id: 'redux.person.utils.addressAccuracy.street',
    defaultMessage: 'Street',
  },
  provinceState: {
    id: 'redux.person.utils.addressAccuracy.provinceState',
    defaultMessage: 'Province/State',
  },
  zipPostalCode: {
    id: 'redux.person.utils.addressAccuracy.zipPostalCode',
    defaultMessage: 'Zip/Postal Code',
  },
  country: {
    id: 'redux.person.utils.addressAccuracy.country',
    defaultMessage: 'Country',
  },
  city: {
    id: 'redux.person.utils.addressAccuracy.city',
    defaultMessage: 'City',
  },
});

const addressLabelMessages = defineMessages({
  none: {
    id: 'redux.person.utils.addressLabel.none',
    defaultMessage: 'None',
  },
});

//The User Statuses used only in Person Search.
export const PERSON_STATUSES = {
  NO_ACCOUNT: 'noAccount',
  INACTIVE: 'inactive',
  ACTIVE: 'active',
  DISABLED: 'disabled',
  LOCKED: 'locked',
};

export const DISABLED_PERSON_STATUSES = ['INACTIVE_DISABLED', 'ACTIVE_DISABLED', 'DISABLED_DELETED'];

export const constants = {
  PERSON: 'person',
  PERSON_FIRSTNAME: 'firstName',
  PERSON_MIDDLENAME: 'middleName',
  PERSON_LASTNAME: 'lastName',
  PERSON_IDENTIFIER: 'identifier',
  PERSON_EMAIL: 'email',
  PERSON_CITY: 'city',
  PERSON_STATE: 'state',
  PERSON_ZIP: 'zip',
  PERSON_COUNTRY: 'country',
  PERSON_PHONE_NUMBER: 'phone',
  PERSON_ORGANIZATIONS: 'organizationIds',
  PERSON_ORGANIZATIONS_KEY: 'organizationsKey',
  PERSON_LOCATION: 'locationId',
  PERSON_LOCATIONS: 'locationIds',
  PERSON_OR_LOCATIONS_IDS: 'orLocationIds',
  PERSON_LOCATIONS_KEY: 'locationsKey',
  PERSON_STATUS: 'userStatuses',
  PERSON_DATE_RANGE: 'personDateRange',
  PERSON_DATE_START: 'dateStart',
  PERSON_DATE_END: 'dateEnd',
  PERSON_MAP_CLUSTER: 'cluster',
  PERSON_MAP_POLYGONS: 'polygons',
  PERSON_MAP_EXCLUDE_POLYGONS: 'excludePolygons',
  PERSON_TYPES: 'personTypes',
  PERSON_TYPE_FILTER: 'personTypeFilter',
  PERSON_EXCLUDES_TYPES: 'excludePersonTypes',
  SHOW_DELETED_PEOPLE_ONLY: 'status',
  ASCEND: 'ascend',
  ASC: 'asc',
  DESCEND: 'descend',
  DESC: 'desc',
  FILTER_STRING: 'searchText',
  FILTER_SEARCH_MINIMUM_LENGTH: 3,
  INCLUDE: 'include',
  TYPES: 'types',
  PERSON_LOCATION_TYPES: 'personLocationTypes',
  MOBILE_LOCATION_SCOPE: 'mobileLocationScope',
  LOCATION_TYPES: 'locationTypes',
  ADDRESS_LABELS: 'addressLabels',
  ADDRESS_ACCURACY: 'addressAccuracies',
  ITINERARY_NAME: 'itineraryNameLike',
  HOTEL_NAME: 'hotelNameLike',
  RECORD_LOCATOR: 'itineraryRecordLocatorLike',
  TRIP_NOTES: 'itineraryNotesLike',
  TRIP_STATUS: 'itineraryStatuses',
  SHOW_DELETED_TRIPS: 'showDeletedTrips',
  EXPATRIATION_NAME: 'expatriationName',
  SHOW_DELETED_EXPATRIATIONS: 'showDeletedExpatriations',
  PERSON_PROFILE_STATUS: 'profileStatuses',
  HORIZON_FILTER_KEY: 'horizonFilterKey',
  SITE_IDS: 'siteIds',
  SITE_TYPE_IDS: 'siteTypeIds',
  SITE_ADDRESS: 'siteAddress',
  CUSTOM_FIELDS: 'customFields',
  ITINERARY_CUSTOM_FIELDS: 'itineraryCustomFields',
  LOCATION_RATINGS: 'locationRatings',
  LOCATION_RATINGS_RANGE: 'locationRatingRange',
  LOCATION_RATINGS_ENABLED_FIELD: 'locationRatingsEnabledField',
  PRIMARY_ORG_ID: 'primaryOrgId',
  INCLUDE_SUB_ORGANIZATIONS: 'includeSubOrganizations',
  HAS_SHARED_EMAIL: 'hasSharedEmail',
  USER_ACCOUNT_LOCKED: 'userAccountLocked',
  EXCLUDE_HOME_SEGMENTS: 'excludeItinerarySegmentsToAddressCountries',
};

/**
 *
 * @param sortField {string} One of constants.PERSON_LASTNAME or constants.PERSON_EMAIL - Sort by name or email
 * @param tableSortDirection {string} One of constants.ASCEND or constants.DESCEND - increasing or decreasing sort order
 * @returns {Array<string>}
 */
export function createSortParamsForPersonsList(sortField, tableSortDirection) {
  const sortDir = tableSortDirection === constants.DESCEND ? constants.DESC : constants.ASC;
  let sortParams = [`${sortField},${sortDir}`];

  if (sortField === constants.PERSON_LASTNAME) {
    sortParams.push(`${constants.PERSON_FIRSTNAME},${sortDir}`); // adds firstName to sort to allow full name sorting
    sortParams.push(`${constants.PERSON_MIDDLENAME},${sortDir}`); // adds middleName to sort to allow full name sorting
  }

  return sortField && tableSortDirection ? sortParams : undefined;
}

/**
 *
 * @param personsListResults
 * @returns {{personsList: *, pageable: {pageNumber: *, pageSize: *, sort: null, totalElements: *, direction: string}}}
 */
export function parsePersonsListResults(personsListResults) {
  const pageable = parsePagedResults(personsListResults);
  const personsList = get(personsListResults, ['data', 'content'], []);

  return {
    pageable,
    personsList,
  };
}

const getFilterParameter = (params, key, valueFunction) =>
  params[key] && params[key].length > 0 ? {[key]: params[key].map(valueFunction)} : undefined;

const getLocationValue = (location) => parseInt(location, 10);
const getSelectedValue = (item) => item?.value || item?.id || item;

const getUserStatusValue = (selectedStatus) => {
  return selectedStatus === PERSON_STATUSES.DISABLED
    ? DISABLED_PERSON_STATUSES
    : findKey(PERSON_STATUSES, (status) => status === selectedStatus);
};

const getDateRange = (params, key) => {
  const dateRange = params[key];

  if (isNil(dateRange)) {
    return undefined;
  }

  const [start, end] = dateRange;

  return {
    dateEnd: end?.utcOffset(0, true).utc().toISOString(),
    dateStart: start?.utcOffset(0, true).utc().toISOString(),
  };
};

const getLikeName = (params, key) => (params[key] ? {[`${key}Like`]: params[key]} : {});

/**
 * transform personTypes for the API
 * since the API only accept personTypes or excludePersonTypes
 *
 * @returns {*}
 */
export function parsePersonTypes(object) {
  if (isNil(object) || isNil(object.include)) {
    return null;
  }

  return {[object.include]: object.types};
}

function formatValuesLikeEqual(value) {
  const {valuesLike, valuesEqual} = value || {};
  const formatted = {
    ...(valuesLike?.length ? {valuesLike} : {}),
    ...(valuesEqual?.length ? {valuesEqual} : {}),
  };

  return Object.keys(formatted).length ? formatted : undefined;
}

// Parse to prep for services ....
export function parseFilterParams(params = {}) {
  if (isNil(params)) {
    return {};
  }
  // Produce correct filter parameters for body of API request
  return Object.keys(params).reduce((previousValue, key) => {
    switch (key) {
      case constants.PERSON_LOCATIONS:
      case constants.PERSON_OR_LOCATIONS_IDS:
        return {...previousValue, ...getFilterParameter(params, key, getLocationValue)};
      case constants.PERSON_ORGANIZATIONS:
      case constants.ADDRESS_LABELS:
      case constants.ADDRESS_ACCURACY:
        return {...previousValue, ...getFilterParameter(params, key, getSelectedValue)};
      case constants.PERSON_STATUS: {
        const statusValues = getFilterParameter(params, key, getUserStatusValue);
        if (statusValues) {
          const flattened = {[key]: statusValues[key].flat()};
          return {...previousValue, ...flattened};
        } else {
          return previousValue;
        }
      }
      case constants.PERSON_DATE_RANGE:
        return {...previousValue, ...getDateRange(params, key)};
      case constants.PERSON_FIRSTNAME:
      case constants.PERSON_LASTNAME:
      case constants.PERSON_MIDDLENAME:
      case constants.EXPATRIATION_NAME:
        return {...previousValue, ...getLikeName(params, key)};
      case constants.SHOW_DELETED_TRIPS:
        return params[constants.SHOW_DELETED_TRIPS]
          ? {...previousValue, itineraryDeletedStatuses: ['ACTIVE', 'DELETED']}
          : {...previousValue};
      case constants.SHOW_DELETED_EXPATRIATIONS:
        return params[constants.SHOW_DELETED_EXPATRIATIONS]
          ? {...previousValue, expatriationDeletedStatuses: ['ACTIVE', 'DELETED']}
          : {...previousValue};
      case constants.PERSON_TYPES:
        return {
          ...previousValue,
          ...parsePersonTypes(params[key]),
        };
      case constants.PERSON_IDENTIFIER:
      case TRAIN_NUMBER:
      case RAILWAY:
        return {...previousValue, [key]: params[key] ? params[key] : undefined};
      case constants.ITINERARY_NAME:
      case constants.RECORD_LOCATOR:
      case FLIGHT_NUMBER_LIKE: // DEPRECATED (HZFE_139) - remove when feature toggle is removed
      case constants.HOTEL_NAME:
      case constants.TRIP_NOTES:
        return {...previousValue, ...(params[key] ? {[key]: params[key]} : {})};
      case FLIGHT_NUMBERS:
        return {...previousValue, [key]: formatValuesLikeEqual(params[key])};
      case AIRLINE_IDS:
        return {
          ...previousValue,
          [key]: params[key]?.length ? map(params[key], 'value') : undefined,
        };
      case AIRLINE_ID: // DEPRECATED (HZFE_139) - remove when feature toggle is removed
      case AIRPORT_ID:
        return {...previousValue, [key]: params[key]?.id || params[key]};
      case constants.SITE_ADDRESS:
        return {...previousValue, siteAddressLike: params[key] ? params[key] : undefined};
      case constants.SITE_IDS:
        return {...previousValue, siteIds: params[key]?.map((site) => site.value)};
      case constants.CUSTOM_FIELDS:
      case constants.ITINERARY_CUSTOM_FIELDS: {
        const formatted = params[key]
          ? Object.entries(params[key]).reduce((acc, [customFieldId, value]) => {
              const formattedValue = formatValuesLikeEqual(value);
              if (formattedValue) {
                acc.push({id: customFieldId, ...formattedValue});
              }
              return acc;
            }, [])
          : undefined;
        return {
          ...previousValue,
          [key]: formatted?.length ? formatted : undefined,
        };
      }

      case constants.LOCATION_RATINGS:
        return params[key]
          ? {
              ...previousValue,
              locationRatingRange: {
                start: params[key][0],
                end: params[key][1],
              },
              // this field is just used to control enabling/disabling/clearing the field range selector when toggled
              // likely ignored by the service but omit anyway
              [constants.LOCATION_RATINGS_ENABLED_FIELD]: undefined,
            }
          : {...previousValue};

      default:
        return {...previousValue, [key]: params[key]};
    }
  }, {});
}

function parseCustomFields(key, params) {
  const customFields = params[key];
  return (
    customFields?.reduce((acc, item) => {
      if (item.id) {
        const toArray = (v) => (v == null ? [] : Array.isArray(v) ? v : [v]);

        const valuesLike = toArray(item.valuesLike);
        const valuesEqual = toArray(item.valuesEqual);

        acc[item.id] = {valuesLike, valuesEqual};
      }

      return acc;
    }, {}) ?? undefined
  );
}

export function mapPersonStatusValues(params, key, personStatuses) {
  return flow([
    (data) => get(data, key),
    (items) =>
      map(items, (value) => {
        if (value?.includes('DISABLED')) {
          return personStatuses.DISABLED;
        }
        return personStatuses[value];
      }),
    (items) => uniq(items),
  ])(params);
}

/**
 * Inverse of parseFilterParams, test should be to run valid options through both and get same results as start
 */
export function parseParamsFilter(params, lookups) {
  if (isEmpty(params)) {
    return {};
  }

  const {addressLabels} = lookups || {};

  // Reverse parse for body of API request back to form filter shape
  return Object.keys(params).reduce((previousValue, key) => {
    switch (key) {
      case constants.PERSON_LOCATIONS:
      case constants.PERSON_OR_LOCATIONS_IDS:
        return {...previousValue, [key]: params[key]?.map((locationIdNumber) => locationIdNumber.toString())};
      case constants.PERSON_ORGANIZATIONS:
        return {
          ...previousValue,
          [key]: params[key],
        };
      case constants.ADDRESS_LABELS:
        return {
          ...previousValue,
          [key]: params[key]?.map((valueId) => {
            const foundValue = addressLabels.find((valueItem) => valueItem.value === valueId);

            return foundValue
              ? foundValue
              : {
                  ...ADDRESS_LABEL.NONE,
                  label: localization.formatMessage(ADDRESS_LABEL.NONE.labelMessage),
                };
          }),
        };
      case constants.ADDRESS_ACCURACY:
        return {
          ...previousValue,
          [key]: params[key]?.map((valueId) => {
            const foundValue = addressAccuracies.find((accuracy) => accuracy.value === valueId);

            return foundValue
              ? {
                  ...foundValue,
                  label: localization.formatMessage(foundValue.labelMessage),
                }
              : {
                  ...ADDRESS_LABEL.NONE,
                  label: localization.formatMessage(ADDRESS_ACCURACY.UNKNOWN.labelMessage),
                };
          }),
        };
      case constants.PERSON_STATUS:
        return {
          ...previousValue,
          [key]: mapPersonStatusValues(params, key, PERSON_STATUSES),
        };
      case 'dateEnd':
        return {
          ...previousValue,
          [constants.PERSON_DATE_RANGE]: [moment.utc(params['dateStart']), moment.utc(params['dateEnd'])],
        };
      case 'dateStart': // return previous because end date parsing handles it
        return previousValue;
      case `${constants.PERSON_FIRSTNAME}Like`:
      case `${constants.PERSON_LASTNAME}Like`:
      case `${constants.PERSON_MIDDLENAME}Like`:
      case `${constants.EXPATRIATION_NAME}Like`:
        return {
          ...previousValue,
          [key.replace('Like', '')]: params[key],
        };
      case 'itineraryDeletedStatuses':
        return {
          ...previousValue,
          [constants.SHOW_DELETED_TRIPS]: isEqual(params[key], [
            TRIP_DELETED_STATUS.ACTIVE.value,
            TRIP_DELETED_STATUS.DELETED.value,
          ]),
        };
      case 'expatriationDeletedStatuses':
        return {
          ...previousValue,
          [constants.SHOW_DELETED_EXPATRIATIONS]: isEqual(params[key], [
            EXPAT_DELETED_STATUS.ACTIVE.value,
            EXPAT_DELETED_STATUS.DELETED.value,
          ]),
        };
      case [FORM_FIELDS.PERSON_LOCATION_TYPES]:
        return {...previousValue, [key]: params[key]};
      case [FORM_FIELDS.MOBILE_LOCATION_SCOPE]:
        return {...previousValue, [key]: params[key]};
      case 'excludePersonTypes':
      case 'personTypes':
        return {
          ...previousValue,
          [constants.PERSON_TYPES]: {
            include: key,
            types: params[key],
          },
        };
      case constants.ITINERARY_NAME:
      case constants.RECORD_LOCATOR:
      case AIRLINE_ID: // DEPRECATED (HZFE_139) - remove when feature toggle is removed
      case AIRLINE_IDS:
      case FLIGHT_NUMBER_LIKE: // DEPRECATED (HZFE_139) - remove when feature toggle is removed
      case FLIGHT_NUMBERS:
      case AIRPORT_ID:
      case constants.HOTEL_NAME:
      case constants.TRIP_NOTES:
      case constants.SITE_IDS:
        return {...previousValue, [key]: params[key]};
      case 'siteAddressLike':
        return {...previousValue, [constants.SITE_ADDRESS]: params[key]};
      case constants.CUSTOM_FIELDS:
      case constants.ITINERARY_CUSTOM_FIELDS:
        return {...previousValue, [key]: parseCustomFields(key, params)};
      default:
        return {...previousValue, [key]: params[key]};
    }
  }, {});
}

export function processDownloadData(downloadDataObject = {}) {
  //Set specific data options based on corresponding 'included' property
  //Do not add 'included' properties to returned object
  return Object.entries(downloadDataObject).reduce((previousValue, [key, value]) => {
    switch (key) {
      case 'accountInfo':
      case 'addresses':
      case 'emails':
      case 'phones':
      case 'emergencyContacts':
      case 'identifiers':
      case 'names':
      case 'mobileLocations':
      case 'tripSegments':
        return {
          ...previousValue,
          [key]: downloadDataObject[[key] + 'Included'] ? value : PERSON_EXPORT_DATA_OPTIONS.EXCLUDE,
        };
      case 'includeOrganizations':
      case 'includePersonTypes':
      case 'optionalFields':
      case 'includeExpatriations':
      case 'includeStopovers':
      case 'includeSites':
      case 'includeAccessPoints':
        return {...previousValue, [key]: value};
      default:
        return {...previousValue};
    }
  }, {});
}

/**
 * Returns the local storage object for 'person-search-download-data' key
 *
 * @returns {*}
 */
export function getDownloadDataLocalStorage() {
  let downloadDataLocalStorage = horizonJSON.parse(horizonLocalStorage.getItem('person-search-download-data')) ?? {};

  return getExportDetailsDefault(downloadDataLocalStorage);
}

export function getExportDetailsDefault(values) {
  return {
    [PERSON_EXPORT_DATA_FIELD_NAMES.NAMES_INCLUDED]: get(values, PERSON_EXPORT_DATA_FIELD_NAMES.NAMES_INCLUDED, true),
    names: get(values, 'names', PERSON_EXPORT_DATA_OPTIONS.PRIMARY),
    includeOrganizations: get(values, 'includeOrganizations', true),
    includePersonTypes: get(values, 'includePersonTypes', false),
    includeStopovers: get(values, 'includeStopovers', false),
    optionalFields: get(values, 'optionalFields', false),
    [PERSON_EXPORT_DATA_FIELD_NAMES.ACCOUNT_INFO_INCLUDED]: get(
      values,
      PERSON_EXPORT_DATA_FIELD_NAMES.ACCOUNT_INFO_INCLUDED,
      true,
    ),
    accountInfo: get(values, 'accountInfo', PERSON_EXPORT_DATA_OPTIONS.PRIMARY),
    [PERSON_EXPORT_DATA_FIELD_NAMES.EMAILS_INCLUDED]: get(values, PERSON_EXPORT_DATA_FIELD_NAMES.EMAILS_INCLUDED, true),
    emails: get(values, 'emails', PERSON_EXPORT_DATA_OPTIONS.PRIMARY),
    [PERSON_EXPORT_DATA_FIELD_NAMES.PHONES_INCLUDED]: get(values, PERSON_EXPORT_DATA_FIELD_NAMES.PHONES_INCLUDED, true),
    phones: get(values, 'phones', PERSON_EXPORT_DATA_OPTIONS.PRIMARY),
    [PERSON_EXPORT_DATA_FIELD_NAMES.EMERGENCY_CONTACTS_INCLUDED]: get(
      values,
      PERSON_EXPORT_DATA_FIELD_NAMES.EMERGENCY_CONTACTS_INCLUDED,
      true,
    ),
    emergencyContacts: get(values, 'emergencyContacts', PERSON_EXPORT_DATA_OPTIONS.PRIMARY),
    [PERSON_EXPORT_DATA_FIELD_NAMES.IDENTIFIERS_INCLUDED]: get(
      values,
      PERSON_EXPORT_DATA_FIELD_NAMES.IDENTIFIERS_INCLUDED,
      true,
    ),
    identifiers: get(values, 'identifiers', PERSON_EXPORT_DATA_OPTIONS.PRIMARY),
    [PERSON_EXPORT_DATA_FIELD_NAMES.ADDRESSES_INCLUDED]: get(
      values,
      PERSON_EXPORT_DATA_FIELD_NAMES.ADDRESSES_INCLUDED,
      true,
    ),
    addresses: get(values, 'addresses', PERSON_EXPORT_DATA_OPTIONS.PRIMARY),
    [PERSON_EXPORT_DATA_FIELD_NAMES.MOBILE_LOCATIONS_INCLUDED]: get(
      values,
      PERSON_EXPORT_DATA_FIELD_NAMES.MOBILE_LOCATIONS_INCLUDED,
      false,
    ),
    mobileLocations: get(values, 'mobileLocations', PERSON_EXPORT_DATA_OPTIONS.LAST),
    [PERSON_EXPORT_DATA_FIELD_NAMES.TRIP_SEGMENTS_INCLUDED]: get(
      values,
      PERSON_EXPORT_DATA_FIELD_NAMES.TRIP_SEGMENTS_INCLUDED,
      false,
    ),
    tripSegments: get(values, 'tripSegments', PERSON_EXPORT_DATA_OPTIONS.ALL),
    [PERSON_EXPORT_DATA_FIELD_NAMES.EXPATRIATIONS_INCLUDED]: get(
      values,
      PERSON_EXPORT_DATA_FIELD_NAMES.EXPATRIATIONS_INCLUDED,
      false,
    ),
    includeSites: get(values, 'includeSites', PERSON_EXPORT_DATA_OPTIONS.ALL),
    [PERSON_EXPORT_DATA_FIELD_NAMES.SITE_DETAILS_INCLUDED]: get(
      values,
      PERSON_EXPORT_DATA_FIELD_NAMES.SITE_DETAILS_INCLUDED,
      false,
    ),
  };
}

/**
 * Sets the local storage object for 'person-search-download-data' key
 *
 * @returns {*}
 */
export function setDownloadDataLocalStorage(storageObject) {
  horizonLocalStorage.setItem('person-search-download-data', JSON.stringify(storageObject));
}

//https://bitbucket.org/crisis24/wc4-api-docs/src/4865e37a178eb8074ff7225273f7e5c3fb761c8e/src/docs/private/alsat/wc4-alsat-ms.yaml#lines-1891
// Jul 21 2023
// Verify user and need for these value to make sure constants are correctly organized
const ADDRESS_ACCURACY = {
  UNKNOWN: {
    value: 'UNKNOWN',
    labelMessage: addressAccuracyMessages.unknown,
  },
  CITY: {
    value: 'CITY',
    labelMessage: addressAccuracyMessages.city,
  },
  COUNTRY: {
    value: 'COUNTRY',
    labelMessage: addressAccuracyMessages.country,
  },
  POSTAL_CODE: {
    value: 'POSTAL_CODE',
    labelMessage: addressAccuracyMessages.zipPostalCode,
  },
  STATE_PROVINCE_TERRITORIES: {
    value: 'STATE_PROVINCE_TERRITORIES',
    labelMessage: addressAccuracyMessages.provinceState,
  },
  STREET_ADDRESS: {
    value: 'STREET_ADDRESS',
    labelMessage: addressAccuracyMessages.street,
  },
  USER_PROVIDED: {
    value: 'USER_PROVIDED',
    labelMessage: addressAccuracyMessages.userProvided,
  },
  NOT_GEOCODED: {
    value: 'NOT_GEOCODED',
    labelMessage: addressAccuracyMessages.notGeocoded,
  },
};

// TODO these values are service driven except for "NONE", will need to review this logic for populating data backwards
const ADDRESS_LABEL = {
  NONE: {
    value: 'NONE',
    labelMessage: addressLabelMessages.none,
  },
};

const EXPAT_DELETED_STATUS = {
  UNKNOWN: {
    value: 'UNKNOWN',
  },
  ACTIVE: {
    value: 'ACTIVE',
  },
  DELETED: {
    value: 'DELETED',
  },
};

const TRIP_DELETED_STATUS = {
  UNKNOWN: {
    value: 'UNKNOWN',
  },
  ACTIVE: {
    value: 'ACTIVE',
  },
  DELETED: {
    value: 'DELETED',
  },
};

const TRIP_STATUS = {
  BOOKED: {
    value: 'BOOKED',
  },
  CANCELLED: {
    value: 'CANCELLED',
  },
};

const MOBILE_LOCATION_SCOPE = {
  UNKNOWN: {
    value: 'UNKNOWN',
  },
  ALL: {
    value: 'ALL',
  },
  LAST: {
    value: 'LAST',
  },
  EXCLUDE: {
    value: 'EXCLUDE',
  },
};

export const PERSON_LOCATION_TYPE = {
  UNKNOWN: {
    value: 'UNKNOWN',
  },
  MOBILE_BACKGROUND_SHARE: {
    value: 'MOBILE_BACKGROUND_SHARE',
  },
  CRISIS_SIGNAL: {
    value: 'CRISIS_SIGNAL',
  },
  SAFETY_CHECK_IN: {
    value: 'SAFETY_CHECK_IN',
  },
  TRIP_AIR: {
    value: 'TRIP_AIR',
    requiredAggregatedPermission: VIEW_TRIPS,
  },
  TRIP_HOTEL: {
    value: 'TRIP_HOTEL',
    requiredAggregatedPermission: VIEW_TRIPS,
  },
  ADDRESS: {
    value: 'ADDRESS',
    requiredAggregatedPermission: VIEW_TRIPS,
  },
  TRIP_RAIL: {
    value: 'TRIP_RAIL',
    requiredAggregatedPermission: VIEW_TRIPS,
  },
  TRIP_CAR: {
    value: 'TRIP_CAR',
    requiredAggregatedPermission: VIEW_TRIPS,
  },
  EXPATRIATION: {
    value: 'EXPATRIATION',
    requiredAggregatedPermission: VIEW_EXPATRIATIONS,
  },
  ACCESS_POINT: {
    value: 'ACCESS_POINT',
    requiredAggregatedPermission: VIEW_ACCESS_POINTS,
  },
  POC: {
    value: 'POC',
    requiredAggregatedPermission: VIEW_SITES,
  },
  OCCUPANT: {
    value: 'OCCUPANT',
    requiredAggregatedPermission: VIEW_SITES,
  },
};

// Returns PERSON_LOCATION_TYPE with ACCESS_POINT conditionally included.
// ACCESS_POINT is only included when the caller has VIEW_ACCESS_POINTS permission
// AND the HZDI_174 feature flag is enabled.
export function getPersonLocationTypes({hasAccessPointPermission} = {}) {
  const includeAccessPoint = hasAccessPointPermission && featureToggle(FEATURES.HZDI_174);
  const {ACCESS_POINT: _removed, ...withoutAccessPoint} = PERSON_LOCATION_TYPE;

  return includeAccessPoint ? PERSON_LOCATION_TYPE : withoutAccessPoint;
}

const PROFILE_STATUS = {
  INCOMPLETE: {
    value: 'INCOMPLETE',
  },
  COMPLETE: {
    value: 'COMPLETE',
  },
  DUPLICATE: {
    value: 'DUPLICATE',
  },
};

const USER_STATUS = {
  NO_ACCOUNT: {
    value: 'NO_ACCOUNT',
  },
  INACTIVE: {
    value: 'INACTIVE',
  },
  ACTIVE: {
    value: 'ACTIVE',
  },
  INACTIVE_DISABLED: {
    value: 'INACTIVE_DISABLED',
  },
  ACTIVE_DISABLED: {
    value: 'ACTIVE_DISABLED',
  },
  DISABLED_DELETED: {
    value: 'DISABLED_DELETED',
  },
  UNKNOWN: {
    value: 'UNKNOWN',
  },
};

// ONE OF LOGIC ... user is expected to have view persons and one or the location type access
const ONE_OF_PERSON_LOCATION_TYPES_PERMISSION_LOGIC = {
  requiredAggregatedPermissions: [VIEW_PERSONS],
  anyOfAggregatedPermissions: [
    VIEW_SAFETY_CHECKIN_SIGNAL_LOCATIONS,
    VIEW_CRISIS_SIGNAL_LOCATIONS,
    VIEW_PERSONS_MOBILE_LOCATION,
    VIEW_TRIPS,
    VIEW_EXPATRIATIONS,
    VIEW_ACCESS_POINTS,
    VIEW_PERSONS_GEOCODED_ADDRESS,
    VIEW_SITES,
  ],
};

export const PERSON_SEARCH_FILTER_FIELD = {
  ADDRESS_ACCURACIES: {
    value: constants.ADDRESS_ACCURACY,
    valuesDict: ADDRESS_ACCURACY,
    requiredAggregatedPermission: VIEW_PERSONS_GEOCODED_ADDRESS,
  },
  ADDRESS_LABELS: {
    // service driven values except for "NONE" value
    value: constants.ADDRESS_LABELS,
    requiredAggregatedPermission: VIEW_PERSONS_GEOCODED_ADDRESS,
  },
  // DEPRECATED (HZFE_139) - remove when feature toggle is removed
  AIRLINE_ID: {
    value: 'airlineId',
    requiredAggregatedPermission: VIEW_TRIPS,
  },
  AIRLINE_IDS: {
    value: 'airlineIds',
    requiredAggregatedPermission: VIEW_TRIPS,
  },
  AIRPORT_ID: {
    value: 'airportId',
    requiredAggregatedPermission: VIEW_TRIPS,
  },
  CLUSTER: {
    value: 'cluster',
  },
  SUPERVISOR: {
    value: 'supervisor',
  },
  LOCATION_CODE: {
    value: 'locationCode',
  },
  RESPONSIBLE_SECURITY_OFFICER: {
    value: 'responsibleSecurityOfficer',
  },
  REGION: {
    value: 'region',
  },
  BUSINESS_UNIT: {
    value: 'businessUnit',
  },
  DATE_END: {
    value: constants.PERSON_DATE_END,
    ...ONE_OF_PERSON_LOCATION_TYPES_PERMISSION_LOGIC,
  },
  DATE_RANGES: {
    value: 'dateRanges',
    ...ONE_OF_PERSON_LOCATION_TYPES_PERMISSION_LOGIC,
  },
  DATE_START: {
    value: constants.PERSON_DATE_START,
    ...ONE_OF_PERSON_LOCATION_TYPES_PERMISSION_LOGIC,
  },
  EXCLUDES_PERSON_TYPES: {
    value: constants.PERSON_EXCLUDES_TYPES,
    requiredAggregatedPermission: VIEW_PERSON_TYPES,
  },
  EXPAT_DELETED_STATUSES: {
    value: 'expatriationDeletedStatuses',
    valuesDict: EXPAT_DELETED_STATUS,
    requiredAggregatedPermission: VIEW_EXPATRIATIONS,
  },
  EXPAT_NAME_LIKE: {
    value: 'expatriationNameLike',
    requiredAggregatedPermission: VIEW_EXPATRIATIONS,
  },
  FIRST_NAME_LIKE: {
    value: 'firstNameLike',
  },
  // DEPRECATED (HZFE_139): Remove the following when feature toggle is removed
  FLIGHT_NUMBER_LIKE: {
    value: 'flightNumberLike',
    requiredAggregatedPermission: VIEW_TRIPS,
  },
  FLIGHT_NUMBERS: {
    value: 'flightNumbers',
    requiredAggregatedPermission: VIEW_TRIPS,
  },
  TRIP_DELETED_STATUSES: {
    value: 'itineraryDeletedStatuses',
    valuesDict: TRIP_DELETED_STATUS,
  },
  TRIP_NAME_LIKE: {
    value: 'itineraryNameLike',
    requiredAggregatedPermission: VIEW_TRIPS,
  },
  TRIP_NOTES_LIKE: {
    value: 'itineraryNotesLike',
    requiredAggregatedPermission: VIEW_TRIPS,
  },
  TRIP_RECORD_LOCATION_LIKE: {
    value: 'itineraryRecordLocatorLike',
    requiredAggregatedPermission: VIEW_TRIPS,
  },
  TRIP_STATUSES: {
    value: 'itineraryStatuses',
    valuesDict: TRIP_STATUS,
    requiredAggregatedPermission: VIEW_TRIPS,
  },
  LAST_NAME_LIKE: {
    value: 'lastNameLike',
  },
  LOCATION_IDS: {
    value: 'locationIds',
    ...ONE_OF_PERSON_LOCATION_TYPES_PERMISSION_LOGIC,
  },
  MIDDLE_NAME_LIKE: {
    value: 'middleNameLike',
  },
  MOBILE_LOCATION_SCOPE: {
    value: 'mobileLocationScope',
    valuesDict: MOBILE_LOCATION_SCOPE,
    ...ONE_OF_PERSON_LOCATION_TYPES_PERMISSION_LOGIC,
  },
  OR_LOCATION_IDS: {
    value: 'orLocationIds',
    ...ONE_OF_PERSON_LOCATION_TYPES_PERMISSION_LOGIC,
  },
  ORGANIZATION_IDS: {
    value: 'organizationIds',
  },
  PERSON_IDS: {
    value: 'personIds',
  },
  PERSON_LOCATION_IDS: {
    value: 'personLocationIds',
    ...ONE_OF_PERSON_LOCATION_TYPES_PERMISSION_LOGIC,
  },
  PERSON_LOCATION_TYPES: {
    value: 'personLocationTypes',
    valuesDict: PERSON_LOCATION_TYPE,
    ...ONE_OF_PERSON_LOCATION_TYPES_PERMISSION_LOGIC,
  },
  PERSON_TYPES: {
    value: 'personTypes',
    requiredAggregatedPermission: VIEW_PERSON_TYPES,
  },
  PERSON_TYPE_FILTER: {
    value: 'personTypeFilter',
    requiredAggregatedPermission: VIEW_PERSON_TYPES,
  },
  POLYGONS: {
    value: 'polygons',
    ...ONE_OF_PERSON_LOCATION_TYPES_PERMISSION_LOGIC,
  },
  EXCLUDE_POLYGONS: {
    value: 'excludePolygons',
    ...ONE_OF_PERSON_LOCATION_TYPES_PERMISSION_LOGIC,
  },
  PROFILE_STATUSES: {
    value: 'profileStatuses',
    valuesDict: PROFILE_STATUS,
  },
  QUICK_SEARCH_TEXT: {
    value: 'quickSearchText',
  },
  RAIL_WAY_NAME_LIKE: {
    value: 'railwayNameLike',
    requiredAggregatedPermission: VIEW_TRIPS,
  },
  SEARCH_TEXT: {
    value: 'searchText',
  },
  SITE_ADDRESS_LIKE: {
    value: 'siteAddressLike',
    requiredAggregatedPermission: VIEW_SITES,
  },
  SITE_IDS: {
    value: 'siteIds',
    requiredAggregatedPermission: VIEW_SITES,
  },
  SITE_TYPE_IDS: {
    value: 'siteTypeIds',
    requiredAggregatedPermission: VIEW_SITES,
  },
  TRAIN_NUMBER_LIKE: {
    value: 'trainNumberLike',
    requiredAggregatedPermission: VIEW_TRIPS,
  },
  USER_STATUSES: {
    value: 'userStatuses',
    valuesDict: USER_STATUS,
  },
  CUSTOM_FIELDS: {
    value: 'customFields',
  },
  ITINERARY_CUSTOM_FIELDS: {
    value: 'itineraryCustomFields',
  },
  HOTEL_NAME: {
    value: 'hotelNameLike',
  },
  INCLUDE_SUB_ORGANIZATIONS: {
    value: constants.INCLUDE_SUB_ORGANIZATIONS,
  },
  USER_ACCOUNT_LOCKED: {
    value: constants.USER_ACCOUNT_LOCKED,
  },
  HAS_SHARED_EMAIL: {
    value: constants.HAS_SHARED_EMAIL,
  },
  EXCLUDE_HOME_SEGMENTS: {
    value: constants.EXCLUDE_HOME_SEGMENTS,
  },
  ///////////
  // UI SIDE ONLY PROPERTIES
  ///////////
  PERSON_DATE_RANGE: {
    value: 'personDateRange',
    ...ONE_OF_PERSON_LOCATION_TYPES_PERMISSION_LOGIC,
  },
  SHOW_DELETED_PEOPLE_ONLY: {
    value: constants.SHOW_DELETED_PEOPLE_ONLY,
  },
  FIRST_NAME: {
    value: constants.PERSON_FIRSTNAME,
  },
  LAST_NAME: {
    value: constants.PERSON_LASTNAME,
  },
  EXPAT_NAME: {
    value: constants.EXPATRIATION_NAME,
    requiredAggregatedPermission: VIEW_EXPATRIATIONS,
  },
  SITE_ADDRESS: {
    value: constants.SITE_ADDRESS,
    requiredAggregatedPermission: VIEW_SITES,
  },
  SHOW_DELETED_EXPATRIATIONS: {
    value: constants.SHOW_DELETED_EXPATRIATIONS,
    requiredAggregatedPermission: VIEW_EXPATRIATIONS,
  },
  SHOW_DELETED_TRIPS: {
    value: constants.SHOW_DELETED_TRIPS,
    requiredAggregatedPermission: VIEW_TRIPS,
  },
  PERSON_IDENTIFIER: {
    value: constants.PERSON_IDENTIFIER,
  },
  LOCATION_RATINGS: {
    value: constants.LOCATION_RATINGS_RANGE,
  },
};

const addressAccuracies = values(ADDRESS_ACCURACY);

/**
 * Transforms a person details object into a format for enhanced recipients
 * @param {Object} person - The detailed person object
 * @returns {Object} person recipient object
 */
export const extractPrimaryPersonFields = (person) => {
  if (!person) return null;

  const primaryName = person.names?.find((name) => name.primary);
  const primaryEmail = person.emails?.find((email) => email.primary) || person.emails?.[0]; // fallback to first email when primary not available
  const primaryOrg = person.organizations?.find((org) => org.primary);

  return {
    id: person.id,
    firstName: primaryName.first || primaryName.firstName,
    lastName: primaryName.last || primaryName.lastName,
    email: primaryEmail.address,
    organization: {
      id: primaryOrg.organizationId,
      name: primaryOrg.organizationName,
    },
  };
};
